import { DEFAULT_WEBSITE_CRAWL_CREDIT_RATE_PER_PAGE } from "@/constants/billing"
import { Response } from "@/types"

import { useCodeerApi, FetchMethod } from "../useCodeerApi"

export interface PlanPriceResponse {
  billing_period: string
  currency: string
  amount_cents?: number | null
}

export interface PlanResponse {
  id: number
  code: string
  display_name: string
  included_credits_monthly: number
  extra_credit_unit_price_usd_per_credit: string
  contact_email?: string | null
  requires_sales_contact: boolean
  extra_credit_pack_options: number[]
  workspace_limit?: number | null
  distribution_limit?: number | null
  knowledge_base_storage_bytes_limit?: number | null
  end_user_limit?: number | null
  api_access_allowed: boolean
  custom_domain_allowed: boolean
  prices: PlanPriceResponse[]
}

export interface SubscriptionResponse {
  organization_id: string
  plan_id: number
  billing_period: string
  status: string
  management_mode: string
  pending_management_mode?: string | null
  pending_collection_method?: string | null
  pending_takeover_effective_date?: string | null
  stripe_subscription_id?: string | null
  current_period_start?: string | null
  current_period_end?: string | null
  trial_end?: string | null
  cancel_at_period_end: boolean
  pending_plan_id?: number | null
  pending_billing_period?: string | null
  pending_effective_date?: string | null
  last_plan_change_at?: string | null
  can_manage_payment_method?: boolean
  can_view_latest_invoice?: boolean
}

export interface BillingOverviewResponse {
  subscription?: SubscriptionResponse | null
  credits_balance: number
  extra_credits_balance: number
  monthly_credits_limit?: number | null
  locked_at?: string | null
  lock_reason?: Record<string, unknown> | null
  website_crawl_credit_rate_per_page?: string | number | null
}

export interface OrganizationLimitUsageItemResponse {
  key: string
  used: number
  limit?: number | null
  unit: "count" | "bytes"
  at_limit: boolean
  error_code: number
  details?: Record<string, number> | null
}

export interface OrganizationLimitUsageResponse {
  items: OrganizationLimitUsageItemResponse[]
}

export interface CreditUsagePointResponse {
  date: string
  value: number
}

export interface CreditUsageResponse {
  range_start: string
  range_end: string
  granularity: "daily" | "weekly"
  total_credits: number
  points: CreditUsagePointResponse[]
  has_data: boolean
}

export interface CreditUsageRecordResponse {
  id: number
  created_at: string
  cost_credits: number
  cost_usd: number
  input_tokens: number
  output_tokens: number
  total_tokens: number
  source?: string | null
  model_display_name?: string | null
  model_provider?: string | null
  model_id?: string | null
  workspace_id?: string | null
  workspace_name?: string | null
  history_id?: number | null
  assistant_conversation_part_id?: number | null
  user_id?: number | null
  user_email?: string | null
  client_user_id?: string | null
  client_user_name?: string | null
  external_user_id?: string | null
  is_navigable: boolean
  disabled_reason?: string | null
}

export interface PlanPrice {
  billingPeriod: string
  currency: string
  amountCents?: number | null
}

export interface Plan {
  id: number
  code: string
  displayName: string
  includedCreditsMonthly: number
  extraCreditUnitPriceUsdPerCredit: string
  contactEmail?: string | null
  requiresSalesContact: boolean
  extraCreditPackOptions: number[]
  workspaceLimit?: number | null
  distributionLimit?: number | null
  knowledgeBaseStorageBytesLimit?: number | null
  endUserLimit?: number | null
  apiAccessAllowed: boolean
  customDomainAllowed: boolean
  prices: PlanPrice[]
}

export interface Subscription {
  organizationId: string
  planId: number
  billingPeriod: string
  status: string
  managementMode: string
  pendingManagementMode?: string | null
  pendingCollectionMethod?: string | null
  pendingTakeoverEffectiveDate?: string | null
  stripeSubscriptionId?: string | null
  currentPeriodStart?: string | null
  currentPeriodEnd?: string | null
  trialEnd?: string | null
  cancelAtPeriodEnd: boolean
  pendingPlanId?: number | null
  pendingBillingPeriod?: string | null
  pendingEffectiveDate?: string | null
  lastPlanChangeAt?: string | null
  canManagePaymentMethod: boolean
  canViewLatestInvoice: boolean
}

export interface BillingOverview {
  subscription?: Subscription | null
  creditsBalance: number
  extraCreditsBalance: number
  monthlyCreditsLimit?: number | null
  lockedAt?: string | null
  lockReason?: Record<string, unknown> | null
  websiteCrawlCreditRatePerPage: number
}

export interface OrganizationLimitUsageItem {
  key: string
  used: number
  limit?: number | null
  unit: "count" | "bytes"
  atLimit: boolean
  errorCode: number
  details?: Record<string, number> | null
}

export interface OrganizationLimitUsage {
  items: OrganizationLimitUsageItem[]
}

export interface CreditUsagePoint {
  date: string
  value: number
}

export interface CreditUsage {
  rangeStart: string
  rangeEnd: string
  granularity: "daily" | "weekly"
  totalCredits: number
  points: CreditUsagePoint[]
  hasData: boolean
}

export interface CreditUsageRecord {
  id: number
  createdAt: string
  costCredits: number
  costUsd: number
  inputTokens: number
  outputTokens: number
  totalTokens: number
  source?: string | null
  modelDisplayName?: string | null
  modelProvider?: string | null
  modelId?: string | null
  workspaceId?: string | null
  workspaceName?: string | null
  historyId?: number | null
  assistantConversationPartId?: number | null
  userId?: number | null
  userEmail?: string | null
  clientUserId?: string | null
  clientUserName?: string | null
  externalUserId?: string | null
  isNavigable: boolean
  disabledReason?: string | null
}

export interface CheckoutSessionSchema {
  checkout_session_id: string
  checkout_url: string
}

export interface BillingPortalSessionSchema {
  portal_url: string
}

export interface InvoiceHostedPageSchema {
  invoice_id: string
  hosted_invoice_url: string
}

export interface CreateSubscriptionCheckoutRequest {
  organization_id: string
  plan_id: number
  billing_period: string
  success_url: string
  cancel_url: string
  allow_promotion_codes?: boolean
}

export interface CreateExtraCreditsCheckoutRequest {
  organization_id: string
  plan_id: number
  credits_purchased: number
  success_url: string
  cancel_url: string
}

export interface CreateBillingPortalSessionRequest {
  organization_id: string
  return_url: string
}

export interface ChangePlanRequest {
  organization_id: string
  plan_id: number
  billing_period: string
  proration_behavior?: "always_invoice" | "create_prorations" | "none"
}

export const transformPlanPrice = (response: PlanPriceResponse): PlanPrice => ({
  billingPeriod: response.billing_period,
  currency: response.currency,
  amountCents: response.amount_cents,
})

export const transformPlan = (response: PlanResponse): Plan => ({
  id: response.id,
  code: response.code,
  displayName: response.display_name,
  includedCreditsMonthly: response.included_credits_monthly,
  extraCreditUnitPriceUsdPerCredit:
    response.extra_credit_unit_price_usd_per_credit,
  contactEmail: response.contact_email ?? null,
  requiresSalesContact: response.requires_sales_contact,
  extraCreditPackOptions: response.extra_credit_pack_options,
  workspaceLimit: response.workspace_limit,
  distributionLimit: response.distribution_limit,
  knowledgeBaseStorageBytesLimit: response.knowledge_base_storage_bytes_limit,
  endUserLimit: response.end_user_limit,
  apiAccessAllowed: response.api_access_allowed,
  customDomainAllowed: response.custom_domain_allowed,
  prices: response.prices.map(transformPlanPrice),
})

export const transformSubscription = (
  response: SubscriptionResponse
): Subscription => ({
  organizationId: response.organization_id,
  planId: response.plan_id,
  billingPeriod: response.billing_period,
  status: response.status,
  managementMode: response.management_mode,
  pendingManagementMode: response.pending_management_mode ?? null,
  pendingCollectionMethod: response.pending_collection_method ?? null,
  pendingTakeoverEffectiveDate:
    response.pending_takeover_effective_date ?? null,
  stripeSubscriptionId: response.stripe_subscription_id ?? null,
  currentPeriodStart: response.current_period_start,
  currentPeriodEnd: response.current_period_end,
  trialEnd: response.trial_end,
  cancelAtPeriodEnd: response.cancel_at_period_end,
  pendingPlanId: response.pending_plan_id ?? null,
  pendingBillingPeriod: response.pending_billing_period ?? null,
  pendingEffectiveDate: response.pending_effective_date ?? null,
  lastPlanChangeAt: response.last_plan_change_at ?? null,
  canManagePaymentMethod: Boolean(response.can_manage_payment_method),
  canViewLatestInvoice: Boolean(response.can_view_latest_invoice),
})

export const transformBillingOverview = (
  response: BillingOverviewResponse
): BillingOverview => ({
  subscription: response.subscription
    ? transformSubscription(response.subscription)
    : null,
  creditsBalance: response.credits_balance,
  extraCreditsBalance: response.extra_credits_balance,
  monthlyCreditsLimit: response.monthly_credits_limit,
  lockedAt: response.locked_at ?? null,
  lockReason: response.lock_reason ?? null,
  websiteCrawlCreditRatePerPage: Number(
    response.website_crawl_credit_rate_per_page ??
      DEFAULT_WEBSITE_CRAWL_CREDIT_RATE_PER_PAGE
  ),
})

export const transformOrganizationLimitUsage = (
  response: OrganizationLimitUsageResponse
): OrganizationLimitUsage => ({
  items: response.items.map((item) => ({
    key: item.key,
    used: item.used,
    limit: item.limit ?? null,
    unit: item.unit,
    atLimit: item.at_limit,
    errorCode: item.error_code,
    details: item.details ?? null,
  })),
})

export const transformCreditUsage = (
  response: CreditUsageResponse
): CreditUsage => ({
  rangeStart: response.range_start,
  rangeEnd: response.range_end,
  granularity: response.granularity,
  totalCredits: response.total_credits,
  points: response.points,
  hasData: response.has_data,
})

export const transformCreditUsageRecord = (
  response: CreditUsageRecordResponse
): CreditUsageRecord => ({
  id: response.id,
  createdAt: response.created_at,
  costCredits: response.cost_credits,
  costUsd: response.cost_usd,
  inputTokens: response.input_tokens,
  outputTokens: response.output_tokens,
  totalTokens: response.total_tokens,
  source: response.source ?? null,
  modelDisplayName: response.model_display_name ?? null,
  modelProvider: response.model_provider ?? null,
  modelId: response.model_id ?? null,
  workspaceId: response.workspace_id ?? null,
  workspaceName: response.workspace_name ?? null,
  historyId: response.history_id ?? null,
  assistantConversationPartId: response.assistant_conversation_part_id ?? null,
  userId: response.user_id ?? null,
  userEmail: response.user_email ?? null,
  clientUserId: response.client_user_id ?? null,
  clientUserName: response.client_user_name ?? null,
  externalUserId: response.external_user_id ?? null,
  isNavigable: response.is_navigable,
  disabledReason: response.disabled_reason ?? null,
})

export const useGetBillingPlansApi = () => {
  return useCodeerApi<Response<PlanResponse[]>>(
    "/billing/plans",
    FetchMethod.Get
  )
}

export const useGetBillingSubscriptionApi = () => {
  return useCodeerApi<Response<BillingOverviewResponse>>(
    "/billing/subscription",
    FetchMethod.Get
  )
}

export const useGetOrganizationCreditUsageApi = (organizationId: string) => {
  return useCodeerApi<Response<CreditUsageResponse>>(
    `/billing/organizations/${organizationId}/credit-usage`,
    FetchMethod.Get
  )
}

export const useGetOrganizationLimitUsageApi = (organizationId: string) => {
  return useCodeerApi<Response<OrganizationLimitUsageResponse>>(
    `/billing/organizations/${organizationId}/limit-usage`,
    FetchMethod.Get
  )
}

export const useGetOrganizationCreditUsageRecordsApi = (
  organizationId: string
) => {
  return useCodeerApi<Response<CreditUsageRecordResponse[]>>(
    `/billing/organizations/${organizationId}/credit-usage-records`,
    FetchMethod.Get
  )
}

export const useCreateSubscriptionCheckoutSessionApi = () => {
  return useCodeerApi<
    Response<CheckoutSessionSchema>,
    CreateSubscriptionCheckoutRequest
  >("/billing/checkout-sessions/subscription", FetchMethod.Post)
}

export const useCreateExtraCreditsCheckoutSessionApi = () => {
  return useCodeerApi<
    Response<CheckoutSessionSchema>,
    CreateExtraCreditsCheckoutRequest
  >("/billing/checkout-sessions/extra-credits", FetchMethod.Post)
}

export const useCreateBillingPortalSessionApi = () => {
  return useCodeerApi<
    Response<BillingPortalSessionSchema>,
    CreateBillingPortalSessionRequest
  >("/billing/customer-portal-session", FetchMethod.Post)
}

export const useGetLatestInvoiceHostedPageApi = () => {
  return useCodeerApi<Response<InvoiceHostedPageSchema>>(
    "/billing/invoices/latest-hosted-page",
    FetchMethod.Get
  )
}

export const useChangePlanApi = () => {
  return useCodeerApi<Response<unknown>, ChangePlanRequest>(
    "/billing/subscriptions/change-plan",
    FetchMethod.Post
  )
}

export interface CancelScheduledPlanChangeRequest {
  organization_id: string
}

export const useCancelScheduledPlanChangeApi = () => {
  return useCodeerApi<Response<unknown>, CancelScheduledPlanChangeRequest>(
    "/billing/subscriptions/cancel-scheduled-change",
    FetchMethod.Post
  )
}
